import React from 'react';
import { Handle, Position, useReactFlow, type NodeProps } from '@xyflow/react';

export interface HubNodeData {
    label: string;
    [key: string]: unknown;
}

/**
 * Hub Node
 *
 * A junction/choice point (doc 06 §1: "n/1 or 1/n" pins — many incoming
 * paths converging, or one path branching into many choices). A single
 * target handle and a single source handle are sufficient for both cases:
 * React Flow allows multiple edges to attach to the same handle by
 * default, and the traversal engine (playMode/TraversalEngine.ts) only
 * ever cares "I've arrived at this node, what's outgoing from here" — it
 * doesn't care how many incoming edges got it there. No pin-count bug here.
 *
 * Label was previously editable only via the Inspector sidebar (which has
 * a combined 'hub'/'group' case) — same on-canvas editing gap Fragment,
 * Note, and Comment had. Added the same double-click-to-edit pattern for
 * consistency, sized for Hub's small circular footprint (a single-line
 * input rather than Fragment/Note's textarea, since the label is short —
 * only its first letter even shows in the compact circle).
 */
export function HubNode({ data, selected, id }: NodeProps) {
    const nodeData = data as HubNodeData;
    const { setNodes } = useReactFlow();
    const [isEditing, setIsEditing] = React.useState(false);
    const [tempLabel, setTempLabel] = React.useState(nodeData.label);

    const handleSaveEdit = () => {
        setNodes((nodes) =>
            nodes.map((n) => (n.id === id ? { ...n, data: { ...n.data, label: tempLabel } } : n))
        );
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <div
                className="nopan nodrag card card-sm shadow-lg p-2"
                style={{ width: 160 }}
                onClick={(e) => e.stopPropagation()}
            >
                <input
                    type="text"
                    value={tempLabel}
                    onChange={(e) => setTempLabel(e.target.value)}
                    className="form-control form-control-sm mb-2"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                />
                <div className="d-flex gap-1">
                    <button
                        type="button"
                        onClick={handleSaveEdit}
                        className="btn btn-sm btn-success flex-fill"
                    >
                        Save
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setTempLabel(nodeData.label);
                            setIsEditing(false);
                        }}
                        className="btn btn-sm btn-outline-secondary flex-fill"
                    >
                        Cancel
                    </button>
                </div>
                <Handle type="target" position={Position.Left} />
                <Handle type="source" position={Position.Right} />
            </div>
        );
    }

    return (
        <div
            className={`d-flex align-items-center justify-content-center ${selected ? 'border-primary shadow-sm' : ''}`}
            style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                backgroundColor: 'var(--tblr-primary)',
                border: selected ? '2px solid var(--tblr-primary)' : '1px solid var(--tblr-border-color)',
                color: 'white',
                fontSize: '0.75rem',
                textAlign: 'center',
                cursor: 'pointer',
            }}
            title={nodeData.label || 'Hub'}
            onDoubleClick={() => setIsEditing(true)}
        >
            <span className="nodrag small">{nodeData.label?.charAt(0).toUpperCase()}</span>
            <Handle type="target" position={Position.Left} />
            <Handle type="source" position={Position.Right} />
        </div>
    );
}

export const hubNodeMeta = {
    type: 'hub' as const,
    label: 'Hub',
    icon: 'ti-git-branch',
    defaultData: { label: 'Hub' } satisfies HubNodeData,
};
